/**
 * Messages between Send and gone.
 *
 * The composer used to be this. It sent, then sat there polling for half a
 * minute, and closed itself on a timer -- so the window of "we do not know yet"
 * was something the user had to sit inside, and the moment they closed it
 * anyway the message left the client's world entirely. If it then failed,
 * nothing said so.
 *
 * This module is that window, moved somewhere it can outlive the composer.
 * `Compose` hands a submission over the instant the daemon accepts one and
 * closes; everything after that -- the hold, the drain, the verdict, the undo
 * -- happens here, and {@link items} is what `ui/Outbox.tsx` draws.
 *
 * # The five states, and which of them are the point
 *
 * `EmailSubmission/get` reports RFC 8621's three `undoStatus` values, which is
 * fewer states than actually exist, so two of these are read out of the pair
 * `(undoStatus, deliveryStatus)` and one out of the clock:
 *
 *  - **held** -- accepted, and the daemon is not allowed to touch it until
 *    `sendAt`. This is the undo window and the only state in which cancelling
 *    is guaranteed to work.
 *  - **sending** -- `sendAt` has passed. The SMTP dialogue may be underway; the
 *    daemon may equally not have got to it yet. The two are indistinguishable
 *    over JMAP and it does not matter, because cancelling may fail either way.
 *  - **sent** -- `final`, `delivered: "yes"`. The bytes were accepted by the
 *    recipient's server.
 *  - **parked** -- `final`, `delivered: "unknown"`. The dialogue was
 *    interrupted, and the daemon deliberately will not retry: a duplicate in
 *    somebody's mailbox cannot be undone, so it stops and leaves the row. This
 *    is the state this whole feature exists for. Nothing else in the client
 *    would ever mention it, the message is not in Sent, and the user has no
 *    reason to suspect anything happened. It is never dismissed on a timer.
 *  - **cancelled** -- withdrawn before anything was said to any server.
 *
 * # Why it lists submissions it did not create
 *
 * {@link adopt} asks each account for *all* its submissions, not just the ones
 * this page sent. A message parked at four in the morning by a tab that has
 * since been closed is exactly the mail that silently did not arrive, and a
 * client that only polled its own sends would show an empty outbox to somebody
 * whose message never went. The list is bounded: the daemon prunes finished
 * rows and keeps parked ones, which is the same judgement made at the other
 * end.
 *
 * # The clock is the server's
 *
 * Every comparison against `sendAt` uses {@link jmap.serverNow}. The undo
 * window is a race with the daemon's drain, and running it against a browser
 * clock that is even a minute out would offer Undo on a message already gone --
 * or hide it from one still sitting in the outbox.
 */

import { onSubmissionAnnounced } from "@transport";
import { computed, signal } from "@preact/signals";

import { call, serverNow, type Invocation } from "./jmap";
import { cancelSubmission, submissions, type Submission, type Withdrawn } from "./mutate";
import * as store from "./store";
import type { Email } from "./types";

export type Stage = "held" | "sending" | "sent" | "parked" | "cancelled";

export interface Item {
  /** Unique across accounts; JMAP ids are not. */
  key: string;
  accountId: string;
  submissionId: string;
  /**
   * The message itself, when the daemon still has it.
   *
   * What "Edit" reopens after a withdrawal, and the reason a cancelled message
   * is not lost: nothing moved it out of Drafts, because the move is applied
   * after a *successful* send.
   */
  emailId: string | null;
  subject: string;
  recipients: string[];
  /** The server's release time in epoch milliseconds, on the server's clock. */
  sendAt: number | null;
  stage: Stage;
  /**
   * What the mail server said, or what went wrong here. Only ever shown for a
   * state the user has to do something about.
   */
  detail: string | null;
  /** When this reached a terminal stage, so a success can fade and a failure cannot. */
  settledAt: number | null;
}

export const items = signal<Item[]>([]);

/**
 * A clock for the countdown, ticked by the poller rather than by each row.
 *
 * One timer for the whole strip. Rows re-render because they read this, which
 * is also what stops a countdown ticking on a screen with nothing on it: when
 * the list is empty the poller stops and so does this.
 */
export const tick = signal(serverNow());

/** Anything the user has not seen the end of. */
export const active = computed(() => items.value.filter((i) => i.stage !== "sent"));

/** Messages that did not arrive and nobody has acknowledged. The alarm. */
export const stuck = computed(() => items.value.filter((i) => i.stage === "parked"));

/**
 * How long a delivered message stays on screen.
 *
 * Long enough to be read as an answer to "did that go?", short enough that the
 * strip is empty again by the time anyone looks back. Failures are exempt --
 * see {@link Item.settledAt}.
 */
const SENT_LINGER_MS = 6_000;

/**
 * How often to look, and why it is two numbers.
 *
 * A message inside its undo window is drawing a countdown in seconds, and a
 * counter that reads 8, 8, 6 is one nobody trusts to be a real deadline -- so
 * while anything is held, every second. Once released there is no countdown and
 * nothing to be quick about: the drain runs on the daemon's own schedule and a
 * client asking three times as often does not make it run sooner.
 */
const TICK_HELD_MS = 1_000;
const TICK_IDLE_MS = 3_000;

function keyOf(accountId: string, submissionId: string): string {
  return `${accountId}:${submissionId}`;
}

/**
 * Which state a submission is in, given the clock.
 *
 * The `held` / `sending` split is drawn at `sendAt` and nowhere else, because
 * that is where the daemon draws it: `outbox::claim` refuses a row before its
 * release time, so up to that instant a withdrawal cannot fail and after it it
 * may. Everything the UI says about Undo follows from this one line.
 */
function stageOf(s: Submission, now: number): Stage {
  if (s.undoStatus === "canceled") return "cancelled";
  if (s.undoStatus === "pending") {
    return s.sendAt !== null && s.sendAt > now ? "held" : "sending";
  }
  return s.delivered === "yes" ? "sent" : "parked";
}

/**
 * What to say about a state the user has to act on.
 *
 * Deliberately silent for the states that are going fine. A running commentary
 * on a message that is simply queued is noise, and noise is what teaches people
 * to stop reading a strip that will one day say something that matters.
 */
function detailOf(s: Submission, stage: Stage): string | null {
  if (stage !== "parked") return null;
  const said = s.smtpReply ? ` The server said: ${s.smtpReply}` : "";
  return (
    `Not confirmed as sent. Check Sent before trying again — it may have gone out.${said}`
  );
}

/**
 * Put a just-created submission on this window's strip.
 *
 * Reached through the transport's announcement rather than called directly:
 * `compose.send` announces *before* it closes the composer, and that ordering
 * is the whole contract — the composer is allowed to close because the message
 * is already visible somewhere that outlives it. On the desktop the composer
 * is its own window, so "somewhere else" is specifically the main window's
 * strip, which is why a plain local call was not enough: it drew the undo
 * button on a strip that died milliseconds later, and the user never saw it.
 * The seed is built from the create response rather than from a read-back, so
 * there is no round trip during which the message is nowhere at all.
 */
export function register(seed: {
  accountId: string;
  submissionId: string;
  emailId: string;
  subject: string;
  recipients: string[];
  sendAt: number | null;
}): void {
  const now = serverNow();
  const item: Item = {
    key: keyOf(seed.accountId, seed.submissionId),
    accountId: seed.accountId,
    submissionId: seed.submissionId,
    emailId: seed.emailId,
    subject: seed.subject.trim() || "(no subject)",
    recipients: seed.recipients,
    sendAt: seed.sendAt,
    stage: seed.sendAt !== null && seed.sendAt > now ? "held" : "sending",
    detail: null,
    settledAt: null,
  };
  items.value = [item, ...items.value.filter((i) => i.key !== item.key)];
  start();
}

// Subscribed at module load, before any send can happen: every window keeps
// its own strip current, so the answer to "did my message go" is the same
// whichever window is asked. In a browser the dispatch is synchronous and
// this is exactly the direct call it replaced.
onSubmissionAnnounced(register);

/** Take one off the strip. Only ever the user's own doing. */
export function dismiss(key: string): void {
  items.value = items.value.filter((i) => i.key !== key);
}

/**
 * Withdraw a send, and say honestly what that did.
 *
 * The button that calls this is only ever offered while the message is `held`,
 * because that is the only state in which the answer is knowable in advance.
 * It can still come back `tooLate` -- the drain may claim the row between the
 * paint and the click -- and that outcome is reported as what it is rather than
 * folded into a failure. A message that has gone has gone.
 */
export async function undo(key: string): Promise<Withdrawn | "error"> {
  const item = items.value.find((i) => i.key === key);
  if (!item) return "gone";

  let outcome: Withdrawn;
  try {
    outcome = await cancelSubmission(item.accountId, item.submissionId);
  } catch (e) {
    patch(key, { detail: `This message could not be withdrawn: ${message(e)}` });
    return "error";
  }

  if (outcome === "done") {
    patch(key, { stage: "cancelled", detail: null, settledAt: serverNow() });
    return "done";
  }
  if (outcome === "tooLate") {
    // Not an error, and not a success. The message was handed to the mail
    // server while the button was on screen, so the truthful thing to do is
    // stop offering the button and say why -- the poll that follows will report
    // whether it actually landed.
    patch(key, {
      stage: "sending",
      detail:
        "Too late — already handed to the mail server.",
    });
    return "tooLate";
  }
  dismiss(key);
  return "gone";
}

function patch(key: string, next: Partial<Item>): void {
  items.value = items.value.map((i) => (i.key === key ? { ...i, ...next } : i));
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ---- keeping it true ---------------------------------------------------------

let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;

/**
 * Run the poller while there is anything to poll, and not otherwise.
 *
 * Idempotent, so every caller that might have created work may simply call it.
 * A self-rescheduling timeout rather than an interval, because the cadence
 * depends on what is on the strip and an interval cannot change its mind.
 */
function start(): void {
  if (running) return;
  running = true;
  void poll();
}

function stop(): void {
  clearTimeout(timer);
  timer = undefined;
  running = false;
}

/**
 * One round: re-read every unfinished submission and age the finished ones off.
 *
 * Reschedules itself, and stops when there is nothing left that changes on its
 * own. Parked rows stay on the strip and are *not* a reason to keep polling:
 * they are terminal by design, and asking about them every second for the life
 * of the tab would be a request per second about a thing that will never move.
 */
async function poll(): Promise<void> {
  tick.value = serverNow();
  expire();

  const pending = items.value.filter((i) => i.stage === "held" || i.stage === "sending");
  const lingering = items.value.some((i) => i.stage === "sent");
  if (pending.length === 0 && !lingering) {
    stop();
    return;
  }

  try {
    if (pending.length === 0) return;
    const byAccount = new Map<string, string[]>();
    for (const item of pending) {
      const ids = byAccount.get(item.accountId) ?? [];
      ids.push(item.submissionId);
      byAccount.set(item.accountId, ids);
    }
    const answers = await Promise.all(
      Array.from(byAccount, ([accountId, ids]) =>
        submissions(accountId, ids).then(
          (list) => ({ accountId, list }),
          // A failed poll is not news about the message. Leaving the row as it
          // was and asking again is right; reporting a network blip as a send
          // failure would send someone to retype a message that is fine.
          () => ({ accountId, list: [] as Submission[] }),
        ),
      ),
    );

    const now = serverNow();
    let next = items.value;
    for (const { accountId, list } of answers) {
      for (const s of list) {
        const key = keyOf(accountId, s.id);
        const seen = stageOf(s, now);
        next = next.map((i) => {
          if (i.key !== key) return i;
          // A message that has been released is never held again. The daemon has
          // no way to put one back, so a row that moved to `sending` and then
          // reads `held` on the next poll is a clock that wobbled -- and acting
          // on it would put an Undo button back over a message already at the
          // mail server, which is the one thing this feature must not do.
          const stage = i.stage === "sending" && seen === "held" ? "sending" : seen;
          return {
            ...i,
            stage,
            sendAt: s.sendAt ?? i.sendAt,
            emailId: s.emailId ?? i.emailId,
            // A `detail` set by a refused withdrawal survives until the state
            // itself changes; the poll must not wipe the one sentence
            // explaining why the button stopped working.
            detail: detailOf(s, stage) ?? (stage === i.stage ? i.detail : null),
            settledAt: stage === "held" || stage === "sending" ? null : (i.settledAt ?? now),
          };
        });
      }
    }
    items.value = next;
    announce(next);
  } finally {
    if (running) {
      const held = items.value.some((i) => i.stage === "held");
      timer = setTimeout(() => void poll(), held ? TICK_HELD_MS : TICK_IDLE_MS);
    }
  }
}

/**
 * Drop delivered messages once they have been on screen long enough to read.
 *
 * Only `sent`. Everything else is either still happening or is a thing that
 * went wrong, and a failure that clears itself is a failure nobody finds out
 * about.
 */
function expire(): void {
  const now = serverNow();
  const kept = items.value.filter(
    (i) => i.stage !== "sent" || i.settledAt === null || now - i.settledAt < SENT_LINGER_MS,
  );
  if (kept.length !== items.value.length) items.value = kept;
}

/**
 * The one thing worth interrupting for.
 *
 * A message that did not arrive is reported once, as a toast, in addition to
 * the strip -- because the strip is in the corner and this is the state that
 * looks like nothing happened. Sends and cancellations get no toast: they are
 * on the strip, which is where the user was already looking.
 */
const announced = new Set<string>();

function announce(list: Item[]): void {
  for (const item of list) {
    if (item.stage !== "parked" || announced.has(item.key)) continue;
    announced.add(item.key);
    store.toast(
      `“${item.subject}” could not be confirmed as sent. It is in the outbox.`,
      "error",
    );
  }
}

// ---- what a previous session left behind ---------------------------------------

let adopting = false;

/**
 * Pick up submissions this page did not create.
 *
 * The parked ones are the reason: they are terminal, invisible everywhere else,
 * and by definition belong to a session that has ended. Anything still in
 * flight is taken too, so two tabs open on the same mailbox agree about what is
 * going out.
 *
 * Finished sends are deliberately skipped. The daemon keeps a window of them so
 * a client can read one back, but a strip that filled with this morning's
 * successful mail on every page load would be a list of things that went fine
 * -- and the one row that did not would be somewhere in it.
 */
export async function adopt(): Promise<void> {
  if (adopting) return;
  adopting = true;
  try {
    const accounts = store.accounts.value;
    const found = await Promise.all(
      accounts.map((a) =>
        submissions(a.id, null).then(
          (list) => ({ accountId: a.id, list }),
          () => ({ accountId: a.id, list: [] as Submission[] }),
        ),
      ),
    );

    const now = serverNow();
    const known = new Set(items.value.map((i) => i.key));
    const fresh: Item[] = [];
    for (const { accountId, list } of found) {
      for (const s of list) {
        const key = keyOf(accountId, s.id);
        if (known.has(key)) continue;
        const stage = stageOf(s, now);
        if (stage === "sent" || stage === "cancelled") continue;
        fresh.push({
          key,
          accountId,
          submissionId: s.id,
          emailId: s.emailId,
          subject: "(no subject)",
          recipients: s.rcptTo,
          sendAt: s.sendAt,
          stage,
          detail: detailOf(s, stage),
          settledAt: stage === "parked" ? now : null,
        });
      }
    }
    if (fresh.length === 0) return;

    items.value = [...items.value, ...fresh];
    await nameThem(fresh);
    announce(items.value);
    start();
  } finally {
    adopting = false;
  }
}

/**
 * Put a subject on rows that arrived as bare submissions.
 *
 * `EmailSubmission` carries an envelope and no headers, so an adopted row knows
 * who a message was addressed to and not what it said. "A message to Sarah did
 * not arrive" is worth something; "*Contract, signed* did not arrive" is what
 * lets somebody decide whether to care right now.
 *
 * Best effort and never awaited by anything that matters -- the row is already
 * on screen and correct without it.
 */
async function nameThem(rows: Item[]): Promise<void> {
  const byAccount = new Map<string, string[]>();
  for (const row of rows) {
    if (!row.emailId) continue;
    const ids = byAccount.get(row.accountId) ?? [];
    ids.push(row.emailId);
    byAccount.set(row.accountId, ids);
  }
  if (byAccount.size === 0) return;

  const calls: Invocation[] = Array.from(byAccount, ([accountId, ids], i): Invocation => [
    "Email/get",
    { accountId, ids, properties: ["id", "subject"] },
    `e${i}`,
  ]);

  let subjects: Map<string, string>;
  try {
    const results = await call(calls);
    subjects = new Map();
    for (let i = 0; i < calls.length; i += 1) {
      const args = results.get(`e${i}`);
      if (!args || args.__error) continue;
      for (const email of (args.list as Pick<Email, "id" | "subject">[] | undefined) ?? []) {
        if (email.subject?.trim()) subjects.set(email.id, email.subject.trim());
      }
    }
  } catch {
    return;
  }

  items.value = items.value.map((i) =>
    i.emailId && subjects.has(i.emailId) ? { ...i, subject: subjects.get(i.emailId)! } : i,
  );
}
